import React from 'react';
import useFadeInSection from '../hooks/useFadeInSection';
import useAppleAnimation from '../hooks/useAppleAnimation';

const AboutSection = () => {
  const fadeInRef = useAppleAnimation('apple-slide-up');

  return (
    <section className="py-20 bg-gray-50" style={{ marginBottom: 0, paddingBottom: '5rem' }}>
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* 회사 소개 텍스트 */}
          <div id="about" className="scroll-mt-[400px] md:scroll-mt-20" ref={fadeInRef}>
            <div className="flex items-center mb-6">
              <img
                src="/images/themoalogo.png"
                alt="The Moa Ventures Logo"
                className="w-12 h-12 md:w-16 md:h-16 mr-4 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.log('로고 이미지 로드 실패:', e.target.src);
                }}
              />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                The Moa Ventures
              </h2>
            </div>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p className="text-lg">
                더모아벤처스는 급변하는 금융 환경 속에서 고객의 자산을 안정적으로 증식시키기 위한 
                <strong className="text-gray-800"> 최적의 투자 솔루션</strong>을 연구하고 제공합니다.
              </p>
              <p>
                특히 고소득 전문직 및 사업자 고객을 위한 <strong className="text-gray-800">맞춤형 절세 포트폴리오</strong>에 
                깊은 전문성을 가지고 있습니다. 법률과 제도에 대한 정확한 이해를 바탕으로,
                최고의 금융 효용을 만들어내는 것을 목표로 합니다.
              </p>
              <p>
                <strong className="text-blue-600">신뢰를 최우선 가치</strong>로 여기며, 모든 투자 과정을 투명하게 공개하고 
                고객과 함께 성장하는 금융 파트너가 되겠습니다.
              </p>
            </div>

            {/* 핵심 가치 */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-600 rounded-full mr-3"></div>
                <span className="text-gray-700 font-medium">전문성과 신뢰성</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-600 rounded-full mr-3"></div>
                <span className="text-gray-700 font-medium">투명한 프로세스</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-600 rounded-full mr-3"></div>
                <span className="text-gray-700 font-medium">고객 맞춤형 서비스</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-600 rounded-full mr-3"></div>
                <span className="text-gray-700 font-medium">지속적인 파트너십</span>
              </div>
            </div>
          </div>

          {/* 통계 및 실적 */}
          <div className="grid grid-cols-2 gap-6">
            {/* 통계 카드들 */}
            <div className="bg-white p-6 rounded-2xl shadow-md text-center hover:shadow-lg transition-shadow" ref={useAppleAnimation('apple-slide-up')}>
              <div className="mb-4">
                <svg className="w-12 h-12 mx-auto text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                </svg>
              </div>
              <p className="text-3xl font-bold text-blue-600 mb-2">1년+</p>
              <p className="text-gray-600 font-medium">투자 자문 경력</p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md text-center hover:shadow-lg transition-shadow" ref={useAppleAnimation('apple-slide-up')}>
              <div className="mb-4">
                <svg className="w-12 h-12 mx-auto text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                </svg>
              </div>
              <p className="text-3xl font-bold text-blue-600 mb-2">10억+</p>
              <p className="text-gray-600 font-medium">누적 자문 금액</p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md text-center hover:shadow-lg transition-shadow" ref={useAppleAnimation('apple-slide-up')}>
              <div className="mb-4">
                <svg className="w-12 h-12 mx-auto text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                </svg>
              </div>
              <p className="text-3xl font-bold text-blue-600 mb-2">98%</p>
              <p className="text-gray-600 font-medium">고객 만족도</p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md text-center hover:shadow-lg transition-shadow" ref={useAppleAnimation('apple-slide-up')}>
              <div className="mb-4">
                <svg className="w-12 h-12 mx-auto text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                </svg>
              </div>
              <p className="text-3xl font-bold text-blue-600 mb-2">전문가</p>
              <p className="text-gray-600 font-medium">세무/법무팀 협력</p>
            </div>
          </div>
        </div>

        {/* 전문팀 소개 */}
        <div className="mt-16 bg-white p-8 md:p-12 rounded-2xl shadow-lg" ref={useAppleAnimation('apple-slide-up')}>
          <h3 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-8">
            전문가팀이 함께합니다
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* 세무 전문가 */}
            <div className="text-center" ref={useAppleAnimation('apple-slide-up')}>
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-2">세무 전문가</h4>
              <p className="text-gray-600 text-base leading-relaxed">
                세법 변화와 공제 제도를 실시간으로 분석하여 
                최적의 절세 전략을 수립합니다.
              </p>
            </div>

            {/* 법무 전문가 */}
            <div className="text-center" ref={useAppleAnimation('apple-slide-up')}>
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16l3-3m-3 3l-3-3"/>
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-2">법무 전문가</h4>
              <p className="text-gray-600 text-base leading-relaxed">
                투자 관련 법규와 규제 사항을 면밀히 검토하여 
                안전하고 합법적인 투자를 보장합니다.
              </p>
            </div>

            {/* 투자 전문가 */}
            <div className="text-center" ref={useAppleAnimation('apple-slide-up')}>
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-2">투자 전문가</h4>
              <p className="text-gray-600 text-base leading-relaxed">
                벤처기업 투자 시장 동향과 리스크를 분석하여 
                수익성과 안정성을 동시에 추구합니다.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;